import React, { useEffect, useCallback } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTableProvider } from 'components/DataTable'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { UnifiedSearchInput } from './UnifiedSearchInput'
import { UnifiedSearchFilters } from './UnifiedSearchFilters'
import { UnifiedSearchResults } from './UnifiedSearchResults'
import {
  useUnifiedSearchParams,
  useUnifiedSearchState,
  useUnifiedSearchAPI,
  useUnifiedSearchOptions,
} from './hooks'

const PAGE_SIZE = 20

// Inner component holds all the search state hooks. It is mounted ONLY while
// the modal is open (see the thin gate wrapper below), so each open remounts
// it and its useState initializers re-seed from the URL — that's what restores
// deep-links and browser back/forward without a props→state resync effect.
const UnifiedSearchModalContent: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isOpen = searchParams.get('search') === 'true'

  // URL parameter management
  const { searchParamsState, updateSearchParams, clearSearchParams } =
    useUnifiedSearchParams()

  // Search state management
  const searchState = useUnifiedSearchState({
    initialState: searchParamsState,
  })

  // Search options (categories, types, sort, products)
  const {
    categoryOptions,
    typeOptions,
    sortOptions,
    productOptions,
    isOppLoading,
    isProductsLoading,
  } = useUnifiedSearchOptions({
    filters: searchState.filters,
  })

  // API and search results
  const searchResults = useUnifiedSearchAPI({
    searchTerm: searchState.debouncedSearchTerm,
    filters: searchState.filters,
    sortBy: searchState.sortBy,
    firstBitePage: searchState.firstBitePage,
    unmatchedPage: searchState.unmatchedPage,
    firstBiteDoorPage: searchState.firstBiteDoorPage,
    unmatchedDoorPage: searchState.unmatchedDoorPage,
    variantPage: searchState.variantPage,
    dealPage: searchState.dealPage,
    selectedTab: searchState.selectedTab,
    pageSize: PAGE_SIZE,
  })

  // URL helper hook
  const { getFirstBiteUrl, getUnmatchedUrl, getDoorUrl } =
    useUnifiedSearchCompanyUrl()

  // Sync local state changes to URL
  useEffect(() => {
    if (!isOpen) return

    updateSearchParams({
      searchTerm: searchState.searchTerm,
      selectedTab: searchState.selectedTab,
      filters: searchState.filters,
      sortBy: searchState.sortBy,
      firstBitePage: searchState.firstBitePage,
      unmatchedPage: searchState.unmatchedPage,
      firstBiteDoorPage: searchState.firstBiteDoorPage,
      unmatchedDoorPage: searchState.unmatchedDoorPage,
      variantPage: searchState.variantPage,
    })
  }, [
    isOpen,
    searchState.searchTerm,
    searchState.selectedTab,
    searchState.filters,
    searchState.sortBy,
    searchState.firstBitePage,
    searchState.unmatchedPage,
    searchState.firstBiteDoorPage,
    searchState.unmatchedDoorPage,
    searchState.variantPage,
    updateSearchParams,
  ])

  // Auto-switch tabs based on search type
  useEffect(() => {
    if (!isOpen || !searchResults.searchType) return

    // If user is searching for an address and not already on a locations tab, switch to FB Locations
    // Only trigger on search type change, not tab change to avoid infinite loops
    if (
      searchResults.searchType === 'address' &&
      searchState.selectedTab !== 'firstbitedoors' &&
      searchState.selectedTab !== 'unmatcheddoors'
    ) {
      searchState.setSelectedTab('firstbitedoors')
    }

    // If user is searching for a SKU and not already on variants tab, switch to Product Variants
    if (
      searchResults.searchType === 'sku' &&
      searchState.selectedTab !== 'variants'
    ) {
      searchState.setSelectedTab('variants')
    }
  }, [isOpen, searchResults.searchType, searchState.setSelectedTab]) // Removed searchState.selectedTab from deps

  // Handle modal close
  const closeModal = useCallback(() => {
    clearSearchParams()
  }, [clearSearchParams])

  // Handle company/door click (variants tab has its own row actions)
  const handleCompanyClick = useCallback(
    (companyOrDoor: any) => {
      // Variants tab uses its own table with row actions, not row clicks
      if (searchState.selectedTab === 'variants') {
        return
      }

      // Deals tab has its own click handler (handleDealClick)
      if (searchState.selectedTab === 'deals') {
        return
      }

      let url = '#'

      if (searchState.selectedTab === 'firstbitedoors') {
        url = getDoorUrl(companyOrDoor, false)
      } else if (searchState.selectedTab === 'unmatcheddoors') {
        url = getDoorUrl(companyOrDoor, true)
      } else if (searchState.selectedTab === 'firstbite') {
        url = getFirstBiteUrl(companyOrDoor)
      } else if (searchState.selectedTab === 'unmatched') {
        url = getUnmatchedUrl(companyOrDoor)
      }

      if (url !== '#') {
        navigate(url)
        closeModal()
      }
    },
    [
      getFirstBiteUrl,
      getUnmatchedUrl,
      getDoorUrl,
      searchState.selectedTab,
      navigate,
      closeModal,
    ]
  )

  // Handle deal row click — navigate to the deal detail page
  const handleDealClick = useCallback(
    (deal: any) => {
      navigate(`/deals/${deal.id}`)
      closeModal()
    },
    [navigate, closeModal]
  )

  // Handle form submission (enter key)
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Search is already triggered by debounce in the API hook
  }, [])

  if (!isOpen) return null

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && closeModal()}
      size="2xl"
      title={
        // Nudge the title down so it reads as vertically centered against the
        // close (X) icon, which sits lower (absolute top-3) in the header.
        <span className="inline-block pt-1.5">Search First Bite</span>
      }
      hideCancelButton
      childrenContainerClassName="max-h-[80vh] overflow-y-auto overflow-x-auto max-w-full w-full"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 space-y-4 pb-2 pt-4">
          {/* Search Input */}
          <TableLinkStyleProvider variant="plain">
            <UnifiedSearchInput
              searchTerm={searchState.searchTerm}
              setSearchTerm={searchState.setSearchTerm}
              onSubmit={handleSearchSubmit}
              searchType={searchResults.searchType}
              isLoading={searchResults.isLoading}
            />
          </TableLinkStyleProvider>

          {/* Divider between the search bar and the enclosed results card */}
          <hr className="border-neutral-300" />

          {/* Tabs and Results (filters render below the tabs via filtersSlot).
              pt-2 adds 8px of breathing room above the card — padding (not
              margin) so it can't collapse with the space-y-4 gap above. */}
          <div className="pt-2">
            <ColumnsStoreProvider
              tableKey="unified-search"
              savedPresetsIdentifier={'UNIFIED_SEARCH_TABLE' as any}
            >
              <DataTableProvider tableKey="unified-search">
                <TableLinkStyleProvider variant="plain">
                  <UnifiedSearchResults
                    filtersSlot={
                      <UnifiedSearchFilters
                        filters={searchState.filters}
                        setFilters={searchState.setFilters}
                        sortBy={searchState.sortBy}
                        setSortBy={searchState.setSortBy}
                        categoryOptions={categoryOptions}
                        typeOptions={typeOptions}
                        sortOptions={sortOptions}
                        productOptions={productOptions}
                        isOppLoading={isOppLoading}
                        isProductsLoading={isProductsLoading}
                        selectedTab={searchState.selectedTab}
                      />
                    }
                    selectedTab={searchState.selectedTab}
                    setSelectedTab={searchState.setSelectedTab}
                    firstBiteCount={searchResults.firstBiteCount}
                    unmatchedCount={searchResults.unmatchedCount}
                    firstBiteDoorCount={searchResults.firstBiteDoorCount}
                    unmatchedDoorCount={searchResults.unmatchedDoorCount}
                    variantCount={searchResults.variantCount}
                    dealCount={searchResults.dealCount}
                    myCompaniesCount={searchResults.myCompaniesCount}
                    myCompaniesResults={searchResults.myCompaniesResults}
                    myCompaniesLoading={searchResults.myCompaniesLoading}
                    variantResults={searchResults.variantResults}
                    dealResults={searchResults.dealResults}
                    currentResults={searchResults.currentResults as any}
                    debouncedSearchTerm={searchState.debouncedSearchTerm}
                    isLoading={searchResults.isLoading}
                    currentTotalRows={searchResults.currentTotalRows}
                    pageSize={PAGE_SIZE}
                    currentPage={searchState.currentPage}
                    setCurrentPage={searchState.setCurrentPage}
                    onRowClick={handleCompanyClick}
                    onDealRowClick={handleDealClick}
                    onVariantDataChange={searchResults.refetchVariants}
                  />
                </TableLinkStyleProvider>
              </DataTableProvider>
            </ColumnsStoreProvider>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// Thin gate: mounts/unmounts the stateful content on open/close so the search
// state is always re-initialized from the URL on open (fixes the back-button
// "search bar glitches out" bug). Keeps hooks out of the always-mounted shell.
const UnifiedSearchModal: React.FC = () => {
  const [searchParams] = useSearchParams()
  const isOpen = searchParams.get('search') === 'true'

  if (!isOpen) return null

  return <UnifiedSearchModalContent />
}

export { UnifiedSearchModal }
export default UnifiedSearchModal
